import { getUtf8ByteLength, normalizeSelectionRange, type TextSelectionRange } from '@/utils/text-kit';
import { type ITextContent, type ITextLayerProps } from '@/version-types';
import { segmentGraphemes } from './base';

/**
 * 判断 grapheme 是否仅由换行符组成。
 *
 * @param text - 待判断的 grapheme。
 * @returns 仅包含换行符时返回 true。
 */
const isNewlineGrapheme = (text: string): boolean => text.length > 0 && text.replace(/\r?\n/g, '') === '';

export type ArtTextCharacterTarget = number | 'all' | null;

/**
 * 将编辑器 UTF-8 选区解析为花字可见字符索引。
 *
 * @param text - 当前完整文本。
 * @param range - 当前 UTF-8 文本选区；null 表示全局调整。
 * @returns 可见字符索引、全局标记或无有效单字符目标时的 null。
 */
export const resolveArtTextCharacterTarget = (
    text: string,
    range: TextSelectionRange | null,
): ArtTextCharacterTarget => {
    if (!range) return 'all';

    const normalized = normalizeSelectionRange(range);
    const totalBytes = getUtf8ByteLength(text);
    if (normalized.start === 0 && normalized.end >= totalBytes) return 'all';

    let offset = 0;
    const visibleRanges = segmentGraphemes(text).flatMap((grapheme) => {
        const start = offset;
        const end = start + getUtf8ByteLength(grapheme);
        offset = end;
        return isNewlineGrapheme(grapheme) ? [] : [{ start, end }];
    });
    if (visibleRanges.length === 0) return null;

    if (normalized.start === normalized.end) {
        if (normalized.start === 0) return 0;
        const target = visibleRanges.findLastIndex((visibleRange) => visibleRange.start < normalized.start);
        return target >= 0 ? target : 0;
    }

    const matched = visibleRanges
        .map((visibleRange, index) => ({ ...visibleRange, index }))
        .filter((visibleRange) => visibleRange.end > normalized.start && visibleRange.start < normalized.end);
    return matched.length === 1 ? matched[0].index : null;
};

/**
 * 根据花字创建 textContents
 * 在内容输入时，若该图层使用了花字，则需要将文本逐字拆分成 textContent 成员
 *
 * @param text - 文本
 * @param layerData - 文本图层数据
 * @returns textContents
 */
export const createTextContentsByArtText = (text: string, layerData: ITextLayerProps) => {
    const { textContents = [], advancedTextMaterial, textMark } = layerData;
    if (!advancedTextMaterial) return textContents;

    const primaryFontFamily = layerData.fontFamily;
    const primaryFontSize = layerData.fontSize;
    const useCount = advancedTextMaterial.material.useCount;

    const ret: ITextContent[] = [];

    // [bugfix]: https://jira.meitu.com/browse/MTXXPRO-64325
    let shouldAttachTextMark = false;
    if (textMark) {
        if (textContents.length === 0) {
            shouldAttachTextMark = true;
        } else {
            const hasNoScopedTextMark = textContents.every((item) => !item.textMark);
            if (hasNoScopedTextMark) {
                shouldAttachTextMark = true;
            }
        }
    }

    let leadingNewlines = '';

    for (const grapheme of segmentGraphemes(text)) {
        if (isNewlineGrapheme(grapheme)) {
            const prevTextContent = ret[ret.length - 1];
            if (prevTextContent) {
                prevTextContent.text += grapheme;
            } else {
                leadingNewlines += grapheme;
            }
            continue;
        }

        const targetIdx = useCount ? ret.length % useCount : 0;
        const textContentPreset = advancedTextMaterial.textContents[targetIdx] ?? advancedTextMaterial.textContents[0];

        const nextContent: ITextContent = {
            ...textContentPreset,
            text: `${leadingNewlines}${grapheme}`,
            fontSize: primaryFontSize,
            fontFamily: primaryFontFamily, // 理论上，应用了花字后，花字文本的 fontFamily 会和 layer 的 fontFamily 一致，所以这里直接使用 layer 的 fontFamily，防止用户有切换过字体
        };
        leadingNewlines = '';

        if (shouldAttachTextMark) {
            nextContent.textMark = textMark;
        }

        ret.push(nextContent);
    }

    return ret;
};

/**
 * 将属性补丁应用到花字选区命中的完整字符内容，避免拆分字符或其附属换行。
 *
 * @param data - 当前花字文本图层数据。
 * @param range - UTF-8 字节偏移表示的文本选区。
 * @param attrs - 要写入命中字符内容的属性补丁。
 * @returns 保持一字符一项结构的 textContents。
 */
export const applyArtTextAttrsToRange = (
    data: ITextLayerProps,
    range: TextSelectionRange,
    attrs: Partial<ITextContent>,
): ITextContent[] | undefined => {
    const textContents = data.textContents;
    if (!textContents?.length) return textContents;

    const normalized = normalizeSelectionRange(range);
    if (normalized.start === normalized.end) return textContents;

    let offset = 0;
    return textContents.map((item) => {
        const start = offset;
        const end = start + getUtf8ByteLength(item.text);
        offset = end;

        if (end <= normalized.start || start >= normalized.end) return item;
        return { ...item, ...attrs, text: item.text };
    });
};
